/*
 * Participant Config client — per DESIGN-PARTICIPANT-CONFIG-V1 §B.5.
 *
 * Reads the resolved `data_collection` section for an event from settings'
 * participant-config endpoint:
 *   GET /api/events/:eventId/participant-config/resolved?section=data_collection
 *
 * settings' backend chassis may not be live yet (see docs/current/
 * MY-API-CHASSIS-DESIGN.md — design-only as of this build), so this seam is
 * built typed-first against the documented contract, with an honest
 * 'unconfigured' base URL when unset — mirroring the existing
 * VITE_TICKETS_API_BASE / ticketsApiBase() pattern already shipped for
 * orders (api.ts). Never mock served to a signed-in user; a fixture for
 * local dev/tests lives separately in participantConfig.fixture.ts and is
 * never imported from this file or from DataProvider.tsx.
 */
import { assistedSession } from '../auth/authSource'

/** Field keys this app can collect on the participant profile, per B.5.
 *  `tshirt_size` + `language` (CMT-2026-08-29-022 / BOARDROOM-2026-08-29-110613):
 *  the wizard's T-shirt-size and language questions are EVENT-LEVEL toggles.
 *  Key names CONFIRMED by settings (SETTINGS-2026-08-29-113258 via
 *  BOARDROOM-2026-08-29-115054) and ground-truthed on the live stage endpoint
 *  2026-08-29: both keys arrive on the resolved read at default-tier
 *  `collect:false` — OFF-BY-CONSTRUCTION everywhere until an event owner opts
 *  in. Semantics: `collect:false` ⇒ the question NEVER renders; an ABSENT key
 *  ⇒ ask (fail-toward-asking, the established questionnaire-gate posture). */
export type ParticipantFieldKey =
  | 'given_name'
  | 'family_name'
  | 'dob'
  | 'gender'
  | 'nationality'
  | 'phone'
  | 'emergency_contact'
  | 'tshirt_size'
  | 'language'
  /** Organiser-authored self-assessed fitness ability question, MOVED from the
   *  event questionnaire to a profile field (settings v1.9.3). Unlike the other
   *  keys it is an AUTHORED question (prompt + Gold/Silver/Bronze levels) and it
   *  may be REQUIRED — see `mapFitnessSelf` + the wizard `fitnessSelf` seam. */
  | 'fitness_self'

const KNOWN_FIELD_KEYS: ReadonlySet<string> = new Set<ParticipantFieldKey>([
  'given_name',
  'family_name',
  'dob',
  'gender',
  'nationality',
  'phone',
  'emergency_contact',
  'tshirt_size',
  'language',
  'fitness_self',
])

export interface DataCollectionFieldConfig {
  key: ParticipantFieldKey
  collect: boolean
  /** Whether the event marks this field REQUIRED. Present on the wire per
   *  DESIGN-PARTICIPANT-CONFIG-V1 §B2 ({collect, required}); historically parsed
   *  but dropped (the seam only answered "does the event collect this?"). Now
   *  carried so a required-but-unanswered field can gate get-ready completeness
   *  (fitness_self, BOARDROOM-2026-09-24-193944). Optional so the many
   *  hand-written {key,collect} fixtures stay valid; mapResolvedDataCollection
   *  always populates it. Absent/non-true ⇒ NOT required. */
  required?: boolean
}

export interface DataCollectionResolved {
  section: 'data_collection'
  eventId: string
  fields: DataCollectionFieldConfig[]
}

/** LIVE wire shape of the resolved endpoint — GROUND-TRUTHED against stage
 *  2026-08-29 (authed probe on e5635953): sections are TOP-LEVEL keys and each
 *  section is a KEYED OBJECT of per-field entries carrying `value` +
 *  `source_tier`, e.g.
 *    { data_collection: { gender: { value: { collect: true, required: true },
 *      source_tier: 'event' }, ... }, schema_version: 1 }
 *  This replaces the earlier fixture-era `{ section, fields: [{key, collect}] }`
 *  assumption, which never matched the live service (the mapper produced an
 *  empty field list ⇒ every toggle silently fell back to ask). `required` and
 *  `source_tier` are present on the wire but deliberately unconsumed here —
 *  this seam only answers "does the event collect this field?". */
interface ResolvedParticipantConfigResponse {
  data_collection?: Record<
    string,
    {
      value?:
        | {
            collect?: unknown
            required?: unknown
          }
        | null
      source_tier?: string
    } | null
  > | null
  schema_version?: number
}

/** One authored fitness level — mirrors the questionnaire `QuestionnaireOption`
 *  contract (myApi.ts): `label` is legacy-flat (settings' v1 fitness rows
 *  {label,value} — e.g. Gold/Silver/Bronze); `label_i18n` is the canonical
 *  locale-keyed shape resolved client-side at render. */
export interface FitnessSelfLevel {
  value: string | number
  label?: string | null
  label_i18n?: Record<string, string> | null
}

/** The organiser-authored fitness_self question, resolved for render + submit.
 *  Shape mirrors the proven questionnaire `QuestionnaireQuestion` (myApi.ts) so
 *  the wizard renders/submits it exactly like the event-questionnaire fitness
 *  question it replaces. `null` ⇒ the event does not collect fitness_self. */
export interface FitnessSelfConfig {
  /** question_id used for the answer submit (questionnaire-answer BFF). */
  questionId: string
  prompt: string
  levels: FitnessSelfLevel[]
  /** REQUIRED per participant-config `data_collection.fitness_self.required`
   *  (set by resolveWizardConfig) — NEVER the questionnaire row's `required`
   *  (boardroom ruling 2026-10-01). mapFitnessSelf always returns false. */
  required: boolean
}

export function participantConfigApiBase(env: Record<string, string | undefined> = import.meta.env): string | null {
  return env.VITE_SETTINGS_API_BASE || null
}

/** Map the wire shape into this app's contract. Pure — unit-tested. Unknown
 *  field keys (future config additions this build doesn't know about yet)
 *  are dropped rather than crashing the readiness computation. Entries whose
 *  `value.collect` is not LITERALLY boolean are also DROPPED (⇒ absent ⇒ the
 *  question is asked): a malformed entry (e.g. a null value object or a
 *  string 'false') must fail TOWARD asking — a `Boolean()` coercion would
 *  silently read some of those as OFF, the wrong posture (CMT-2026-08-29-022). */
export function mapResolvedDataCollection(
  eventId: string,
  json: ResolvedParticipantConfigResponse,
): DataCollectionResolved {
  const fields: DataCollectionFieldConfig[] = Object.entries(json.data_collection ?? {})
    .filter(
      (entry): entry is [ParticipantFieldKey, { value: { collect: boolean; required?: unknown } }] =>
        KNOWN_FIELD_KEYS.has(entry[0]) && typeof entry[1]?.value?.collect === 'boolean',
    )
    // `required` is a soft read: only a LITERAL true marks the field required —
    // any other/malformed value ⇒ not required (fail toward NOT blocking, the
    // mirror of collect's fail-toward-asking; a malformed flag must not silently
    // gate get-ready).
    .map(([key, cfg]) => ({ key, collect: cfg.value.collect, required: cfg.value.required === true }))
  return { section: 'data_collection', eventId, fields }
}

/** The slice of the questionnaire BFF payload (myApi `QuestionnaireStatus` —
 *  settings' /svc resolved pass-through) that the fitness seam reads. Structural
 *  so unit fixtures and the myApi type both satisfy it without an import cycle. */
export interface QuestionnaireStatusLike {
  active?: unknown
  show?: unknown
  submitted?: unknown
  questions?: unknown
}

/** Is ONE resolved-questionnaire question answered? MIRRORS my-api
 *  store.ts fitnessAnsweredFrom exactly, so FE ask and server gate can never
 *  disagree: ONLY the per-question signal counts — a boolean `answered`/
 *  `submitted`/`is_answered`/`has_answer` that is true, or a present
 *  `answer`/`answer_value`/`value` (not null/undefined/''). A row with NO
 *  per-question signal is NOT answered. The resolved-level `submitted` is
 *  NEVER read (settings #630): it is some(answered), and the fitness_self row
 *  now carries the participant's latest fitness answer from ANY event, so a
 *  fitness answer given elsewhere makes `submitted` true without a single
 *  event question being answered. */
export function questionAnswered(question: object): boolean {
  const row = question as Record<string, unknown>
  const flagKeys = ['answered', 'submitted', 'is_answered', 'has_answer'] as const
  const valueKeys = ['answer', 'answer_value', 'value'] as const
  const hasValue = (v: unknown): boolean => v !== undefined && v !== null && v !== ''
  return flagKeys.some((k) => row[k] === true) || valueKeys.some((k) => hasValue(row[k]))
}

/** Is this questionnaire row the fitness_self question? The canonical qtype
 *  'fitness_self' (settings mints UUID question_ids), with the tolerant
 *  question_id-contains-'fitness' fallback for legacy rows — the SAME match as
 *  my-api's isFitnessQuestionRow, so FE and server agree on which row it is. */
export function isFitnessQuestion(q: unknown): boolean {
  if (!q || typeof q !== 'object') return false
  const row = q as Record<string, unknown>
  return (
    row.qtype === 'fitness_self' ||
    (typeof row.question_id === 'string' && row.question_id.toLowerCase().includes('fitness'))
  )
}

/** The EVENT questionnaire's own questions: every resolved question EXCEPT the
 *  fitness_self row. fitness_self is a PROFILE-LEVEL field (boardroom ruling
 *  2026-10-01) asked by the profile wizard; the questionnaire is only its
 *  answer store, so it never renders in the "Event questionnaire" form nor
 *  counts toward that form's completeness. */
export function eventQuestionnaireQuestions<Q>(questions: readonly Q[] | null | undefined): Q[] {
  return (Array.isArray(questions) ? questions : []).filter(
    (q) => !!q && typeof q === 'object' && !isFitnessQuestion(q),
  )
}

/** Is the EVENT questionnaire COMPLETE for this participant? Computed over the
 *  NON-fitness questions only: every REQUIRED one answered per its OWN
 *  per-question signal (questionAnswered — never the resolved-level
 *  `submitted`). With no required non-fitness question there is nothing left
 *  to do ⇒ complete (documented decision): the get-ready step reads done, but
 *  never PURELY off a fitness answer, because the fitness row is excluded and
 *  `submitted` is not read. With NO non-fitness question at all the Event
 *  questionnaire step is absent (questionnaireStepFromStatus). */
export function questionnaireComplete(status: QuestionnaireStatusLike | null | undefined): boolean {
  if (!status || typeof status !== 'object') return false
  const questions = eventQuestionnaireQuestions(
    Array.isArray(status.questions) ? (status.questions as unknown[]) : [],
  ) as Record<string, unknown>[]
  return questions.filter((q) => q.required === true).every((q) => questionAnswered(q))
}

/** Resolve the organiser-authored fitness_self question's RENDER data from the
 *  questionnaire — THE SEAM for the questionnaire-side wire shape.
 *
 *  SPLIT OF SIGNALS (boardroom ruling 2026-10-01; settings
 *  participantConfigResolver.ts:44-55): whether the wizard ASKS fitness and
 *  whether it is REQUIRED come ONLY from participant-config
 *  `data_collection.fitness_self` {collect, required} (resolveWizardConfig).
 *  The questionnaire is just the answer + scoring store, so this seam supplies
 *  only what that field cannot: the question_id to submit into, the prompt and
 *  the authored levels (`qtype:'fitness_self'` row of settings' /svc resolved,
 *  via the my-api BFF). The row's own `required` is IGNORED — `required` here
 *  is always false and the caller sets it from participant-config.
 *  `question_id` is a UUID and settings' answer endpoint 400-rejects any
 *  non-UUID, so there is NO fallback id.
 *
 *  Returns null (no wizard step, no gap) when: the questionnaire is not active;
 *  `show` is explicitly false (datalake says this participant no longer needs
 *  asking); the fitness row is ANSWERED per questionAnswered (its per-question
 *  signal only — the exact my-api rule, so the wizard never hides a step the
 *  server gate still blocks on, BOARDROOM-2026-09-28-105149 item 1); no
 *  fitness_self row is present; or the row carries no renderable levels (never
 *  fabricate an empty choice list). */
export function mapFitnessSelf(status: QuestionnaireStatusLike | null | undefined): FitnessSelfConfig | null {
  if (!status || typeof status !== 'object') return null
  if (status.active !== true) return null
  if (status.show === false) return null
  const questions = Array.isArray(status.questions) ? status.questions : []
  const row = questions.find((q): q is Record<string, unknown> => isFitnessQuestion(q))
  if (!row) return null
  if (questionAnswered(row)) return null
  const questionId = typeof row.question_id === 'string' && row.question_id ? row.question_id : null
  if (!questionId) return null
  const rawOptions = Array.isArray(row.options) ? row.options : []
  const levels: FitnessSelfLevel[] = rawOptions
    .filter((o): o is Record<string, unknown> => !!o && typeof o === 'object')
    .filter((o) => o.value === 0 || typeof o.value === 'string' || typeof o.value === 'number')
    .map((o) => ({
      value: o.value as string | number,
      label: typeof o.label === 'string' ? o.label : null,
      label_i18n:
        o.label_i18n && typeof o.label_i18n === 'object'
          ? (o.label_i18n as Record<string, string>)
          : null,
    }))
  // No authored levels ⇒ nothing renderable; do not fabricate a question.
  if (levels.length === 0) return null
  const prompt = typeof row.prompt === 'string' ? row.prompt : ''
  // REQUIRED is participant-config's call (resolveWizardConfig), never the row's.
  return { questionId, prompt, levels, required: false }
}

export async function fetchParticipantConfigDataCollection(
  eventId: string,
  base: string,
  idToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<DataCollectionResolved> {
  const url =
    `${base.replace(/\/$/, '')}/api/events/${encodeURIComponent(eventId)}` +
    `/participant-config/resolved?section=data_collection`
  // Settings-direct: never under a help-desk assisted session (the as1
  // session must not leave my-api; there is no Cognito token).
  if (assistedSession()) throw new Error('participant-config settings-direct read unavailable under an assisted session')
  const res = await fetchImpl(url, { headers: { Authorization: `Bearer ${idToken}` } })
  if (!res.ok) throw new Error(`participant-config resolved fetch failed (${res.status})`)
  return mapResolvedDataCollection(eventId, (await res.json()) as ResolvedParticipantConfigResponse)
}

export function collectsField(config: DataCollectionResolved, key: ParticipantFieldKey): boolean {
  return config.fields.some((f) => f.key === key && f.collect)
}

/** Whether the event marks `key` REQUIRED (only counts when it also collects
 *  it — a required flag on an uncollected field is meaningless). */
export function fieldRequired(config: DataCollectionResolved, key: ParticipantFieldKey): boolean {
  return config.fields.some((f) => f.key === key && f.collect && f.required === true)
}

/** Resolve an authored fitness level's display label for the given UI locale —
 *  same resolution order as the questionnaire renderer (myApi.QuestionnaireOption
 *  contract): flat `label` (legacy, wins when non-empty) → `label_i18n[lang]` →
 *  `label_i18n.en` → first non-empty i18n value → the raw value stringified. */
export function fitnessLevelLabel(level: FitnessSelfLevel, lang: string): string {
  if (level.label != null && level.label !== '') return level.label
  const i18n = level.label_i18n
  if (i18n) {
    const pick = i18n[lang] ?? i18n.en ?? Object.values(i18n).find((v) => v != null && v !== '')
    if (pick != null && pick !== '') return pick
  }
  return String(level.value)
}
